import React, { useContext, useState } from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import CustomDialog from '../custom-dialog/custom-dialog';
import {
  DialogTitle,
  Typography,
  DialogContent,
  Stack,
  Button,
  CircularProgress,
  DialogActions,
  TextField,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { format } from 'date-fns';
import { useSnackBar } from '../../../contexts/snackbar';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface FreezeOrderDialogProps {
  order: OrderType;
  setOrder: (order: OrderType) => void;
  onClose: () => void;
}

function FreezeOrderDialog({
  order,
  setOrder,
  onClose,
}: FreezeOrderDialogProps) {
  const [reason, setReason] = useState('');
  const [until, setUntil] = useState<Dayjs | null>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const freeze = async (date: Date, reason: string) => {
    try {
      setLoading(true);
      const response = await axios.post<OrderType>(
        `${API_ENDPOINT}/api/v1/order/${order.id}/freeze/`,
        { until_datetime: date.toISOString(), reason: reason },
        config
      );
      showMessage(`Order frozen until ${format(date, 'dd-MM-yyyy')}!`);
      setOrder({ ...order, ...response.data });
      onClose();
    } catch {
      showMessage('Failed to freeze order', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (until == null) {
      showMessage('Please choose a date', 'error');
      return;
    }
    const untilDate = until.toDate();
    const now = new Date();

    if (!reason.trim()) {
      setError('Please enter a reason.');
      return;
    }

    if (!until || isNaN(untilDate.getTime()) || untilDate <= now) {
      showMessage('Choose a future date', 'error');
      return;
    }

    try {
      setLoading(true);
      await freeze(untilDate, reason);
    } catch (err) {
      setError('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomDialog open={true} onClose={onClose} maxWidth="sm">
      <DialogTitle>
        Freeze Order
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
          Order #{order.order_id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Unfreeze on"
              value={until}
              onChange={(newValue) => setUntil(newValue)}
            />
          </LocalizationProvider>

          <TextField
            label="Reason"
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Freeze Order'}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}

export default FreezeOrderDialog;
